import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Scan, Camera, Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FaceRecognitionPage = () => {
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [matches, setMatches] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleScan = async () => {
    if (!previewUrl) {
      toast.error('Please upload a photo first');
      return;
    }

    setScanning(true);
    setScanned(false);
    
    try {
      const { data } = await axios.post(
        `${API}/api/face-recognition/search`,
        { face_data: previewUrl },
        { withCredentials: true }
      );
      
      setMatches(data.photos.map(p => ({
        ...p,
        image_url: `${API}${p.image_url}`
      })));
      setScanned(true);
      
      if (data.matches_found > 0) {
        toast.success(`Found ${data.matches_found} matching photos!`);
      } else {
        toast.info('No matching photos found');
      }
    } catch (e) {
      console.error('Scan error:', e);
      toast.error(e.response?.data?.detail || 'Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const resetScan = () => {
    setPreviewUrl(null);
    setScanned(false);
    setMatches([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="face-recognition-page">
      {/* Header */}
      <section className="py-12 text-center">
        <div className="container-luxury">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A14A]/10 rounded-full mb-6">
            <AlertCircle className="w-4 h-4 text-[#C9A14A]" />
            <span className="text-[#C9A14A] text-xs uppercase tracking-wider">MOCKED Feature</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-light text-white mb-4">
            Face Recognition
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Upload your photo and our AI will find all your photos from our gallery.
            <br />
            <span className="text-gray-500 text-sm">(This is a simulated demo)</span>
          </p>
        </div>
      </section>

      {/* Scanner */}
      <section className="pb-24">
        <div className="container-luxury">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Upload Section */}
              <div className="card-luxury p-6 md:p-8">
                <h3 className="font-['Playfair_Display'] text-xl text-white mb-6 flex items-center gap-3">
                  <Camera className="w-5 h-5 text-[#C9A14A]" />
                  Upload Your Photo
                </h3>

                <div className="relative">
                  {previewUrl ? (
                    <div className="relative aspect-square overflow-hidden rounded-none border border-[#333]">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Scanning overlay */}
                      {scanning && (
                        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                          <div className="relative w-3/4 h-3/4 border-2 border-[#C9A14A] rounded-sm">
                            <div className="scan-line absolute left-0 right-0 h-1 bg-[#C9A14A] shadow-[0_0_10px_#C9A14A]"></div>
                            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-[#C9A14A]"></div>
                            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-[#C9A14A]"></div>
                            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-[#C9A14A]"></div>
                            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-[#C9A14A]"></div>
                          </div>
                          <p className="text-[#C9A14A] mt-4 text-sm uppercase tracking-wider">Scanning...</p>
                        </div>
                      )}
                      
                      {/* Scanned success overlay */}
                      {scanned && !scanning && (
                        <div className="absolute top-4 right-4">
                          <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-3 py-1 rounded-full text-xs flex items-center gap-2">
                            <CheckCircle className="w-3 h-3" />
                            Scanned
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <label 
                      className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-[#333] hover:border-[#C9A14A] transition-colors cursor-pointer"
                      data-testid="upload-area"
                    >
                      <Upload className="w-12 h-12 text-gray-500 mb-4" />
                      <span className="text-gray-400 text-sm">Click to upload</span>
                      <span className="text-gray-500 text-xs mt-1">or drag and drop</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        data-testid="file-input"
                      />
                    </label>
                  )}
                </div>

                <div className="flex gap-4 mt-6">
                  {previewUrl && (
                    <Button
                      onClick={resetScan}
                      className="flex-1 bg-[#222] text-white border border-[#333] hover:border-red-500 hover:text-red-400"
                      data-testid="reset-button"
                    >
                      Reset
                    </Button>
                  )}
                  <Button
                    onClick={handleScan}
                    className="flex-1 btn-gold"
                    disabled={!previewUrl || scanning}
                    data-testid="scan-button"
                  >
                    {scanning ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Scanning...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Scan className="w-4 h-4" />
                        Scan Face
                      </div>
                    )}
                  </Button>
                </div>
              </div>

              {/* Results Section */}
              <div className="card-luxury p-6 md:p-8">
                <h3 className="font-['Playfair_Display'] text-xl text-white mb-6 flex items-center gap-3">
                  <Scan className="w-5 h-5 text-[#C9A14A]" />
                  Matching Photos
                </h3>

                {!scanned && !scanning && (
                  <div className="aspect-square flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 bg-[#222] rounded-full flex items-center justify-center mb-4">
                      <Scan className="w-8 h-8 text-gray-500" />
                    </div>
                    <p className="text-gray-500 text-sm">Upload a photo and scan to find matches</p>
                  </div>
                )}

                {scanning && (
                  <div className="aspect-square flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-[#C9A14A]/10 rounded-full flex items-center justify-center mb-4 animate-pulse">
                      <Loader2 className="w-8 h-8 text-[#C9A14A] animate-spin" />
                    </div>
                    <p className="text-[#C9A14A] text-sm">Analyzing facial features...</p>
                  </div>
                )}

                {scanned && !scanning && (
                  <div>
                    {matches.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                        {matches.map((photo, index) => (
                          <div
                            key={photo.id || index}
                            className="aspect-square overflow-hidden group"
                            data-testid={`match-${index}`}
                          >
                            <img
                              src={photo.image_url}
                              alt={`Match ${index + 1}`}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="aspect-square flex flex-col items-center justify-center text-center">
                        <div className="w-16 h-16 bg-[#222] rounded-full flex items-center justify-center mb-4">
                          <AlertCircle className="w-8 h-8 text-gray-500" />
                        </div>
                        <p className="text-gray-400 text-sm mb-2">No matching photos found</p>
                        <p className="text-gray-500 text-xs">Try uploading a different photo</p>
                      </div>
                    )}
                    
                    {matches.length > 0 && (
                      <div className="mt-4 p-3 bg-[#222] border border-[#333]">
                        <p className="text-[#C9A14A] text-sm">
                          Found {matches.length} photos matching your face
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FaceRecognitionPage;
